import { useContext, useEffect, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import SocketIOContext from '../context/socketIOContext';
import {
  subscribeRequestActivity,
  markSlowRequest,
} from '../utils/requestActivityStore';

// Only show the bar once a request has been running for a moment, so quick
// (cache-hit) responses never make it flash.
const SHOW_DELAY_MS = 200;

// A thin fixed progress bar at the very top of the viewport. It appears while
// HTTP requests are in flight and turns amber + pulses once the backend signals
// (over the WebSocket, the instant a cache miss is detected) that the request
// has to query the database - so the user sees "this is slow because of the DB"
// while still waiting, without a popup or header clutter.
export const RequestActivityBar = () => {
  const socket = useContext(SocketIOContext);
  const [{ active, slow, revalidating }, setState] = useState({
    active: 0,
    slow: false,
    revalidating: false,
  });
  const [delayedActive, setDelayedActive] = useState(false);

  // Mirror the request-activity store.
  useEffect(() => subscribeRequestActivity(setState), []);

  // Delay showing the bar for in-flight requests so fast requests stay invisible.
  useEffect(() => {
    if (active > 0) {
      const timer = setTimeout(() => setDelayedActive(true), SHOW_DELAY_MS);
      return () => clearTimeout(timer);
    }
    setDelayedActive(false);
    return undefined;
  }, [active]);

  // Early cache-miss signal -> mark the current activity slow (amber).
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return undefined;
    const off = socket.on('cache:slow-query', () => markSlowRequest());
    return () => {
      if (typeof off === 'function') off();
    };
  }, [socket]);

  // Show the bar while a request is in flight OR while cached data is being
  // reconciled with the database in the background.
  const visible = delayedActive || revalidating;
  // Amber (+ pulse) means "the database is involved": a slow query or a background
  // reconciliation of cached data.
  const amber = slow || revalidating;

  if (!visible) return null;

  return (
    <LinearProgress
      color={amber ? 'warning' : 'primary'}
      aria-label={
        revalidating
          ? 'Abgleich mit Datenbank läuft'
          : slow
            ? 'Datenbankabfrage läuft'
            : 'Lädt'
      }
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: theme => theme.zIndex.snackbar + 1,
        ...(amber
          ? {
              animation: 'requestActivitySlowPulse 1.2s ease-in-out infinite',
              '@keyframes requestActivitySlowPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.55 },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }
          : {}),
      }}
    />
  );
};

export default RequestActivityBar;
